/**
 * iter 58 — Reset Password page (custom flow, Supabase bypassed).
 *
 * Public route at /reset-password?token=...  The user lands here from the
 * link in our SendGrid email. We validate the token + new password client-
 * side, then POST to the backend which calls Supabase admin API to set the
 * password and marks the token consumed.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, CheckCircle } from "@phosphor-icons/react";
import { api, formatErr } from "../lib/api";
import PasswordInput from "../components/PasswordInput";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState("");
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [email, setEmail] = useState("");

  useEffect(() => {
    setToken(params.get("token") || "");
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw1.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (pw1 !== pw2)    { setErr("Passwords don't match.");                  return; }
    if (!token)         { setErr("Reset link is missing its token.");        return; }
    setBusy(true);
    try {
      const r = await api.post("/auth/reset-password", { token, new_password: pw1 });
      setEmail(r.data?.email || "");
      setDone(true);
      setTimeout(() => navigate("/login"), 3000);
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[#0A0A0A] text-white">
        <img src="/logbook-logo.png" alt="LogBook Legal" className="h-12 w-auto" />
        <div className="space-y-6 max-w-md">
          <div className="label-xs text-white/60">RECOVERY · STEP 2</div>
          <h1 className="heading text-5xl leading-[0.95]">Choose a new<br/>password.</h1>
          <p className="text-white/70 leading-relaxed">Pick something at least 8 characters. The reset link can only be used once.</p>
        </div>
        <div className="text-xs text-white/40 mono">© 2026 LOGBOOK LEGAL</div>
      </div>
      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md space-y-6">
          <Link to="/login" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground" data-testid="reset-back-link">
            <ArrowLeft size={12} /> Back to sign in
          </Link>
          <div>
            <div className="label-xs">RECOVERY</div>
            <h2 className="heading text-3xl mt-1">Set new password</h2>
          </div>

          {done ? (
            <div className="space-y-3" data-testid="reset-done">
              <div className="badge badge-default w-full justify-center bg-emerald-50 text-emerald-800 border-emerald-300">
                <CheckCircle size={14} className="mr-1" /> Password updated
              </div>
              <p className="text-sm text-muted-foreground">
                {email
                  ? <>Your password for <strong>{email}</strong> has been changed.</>
                  : <>Your password has been changed.</>
                } Redirecting to sign-in…
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
              {!token && (
                <div className="badge badge-red w-full justify-center" data-testid="reset-missing-token">
                  This page needs a token from your email link.
                </div>
              )}
              <div>
                <label className="label-xs block mb-2">NEW PASSWORD</label>
                <PasswordInput
                  value={pw1}
                  onChange={(e) => setPw1(e.target.value)}
                  required
                  minLength={8}
                  autoFocus
                  data-testid="reset-pw1-input"
                />
              </div>
              <div>
                <label className="label-xs block mb-2">CONFIRM NEW PASSWORD</label>
                <PasswordInput
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  required
                  minLength={8}
                  data-testid="reset-pw2-input"
                />
              </div>
              {err && <div className="badge badge-red w-full justify-center" data-testid="reset-error">{err}</div>}
              <button
                type="submit"
                className="btn btn-primary w-full justify-center"
                disabled={busy || !token || pw1.length < 8 || pw1 !== pw2}
                data-testid="reset-submit"
              >
                {busy ? "UPDATING…" : "UPDATE PASSWORD →"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
